import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
    getRouteSeo,
    siteUrl,
    ogImage,
    ogImageWidth,
    ogImageHeight,
    ogImageAlt,
} from '../config/seoConfig';

const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
    let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
};

const setLink = (rel: string, href: string) => {
    let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
    }
    el.setAttribute('href', href);
};

/**
 * Keeps the document title, meta description, canonical, lang and Open
 * Graph tags in sync with the current route and language. Rendered once
 * inside Layout.
 *
 * This covers client-side navigation. The initial HTML that crawlers see
 * is produced at build time by scripts/prerender.mjs — keep the two in
 * step when adding tags.
 */
const Seo: React.FC = () => {
    const { pathname } = useLocation();
    const { language } = useLanguage();

    useEffect(() => {
        if (typeof document === 'undefined') return;

        const seo = getRouteSeo(pathname);
        const title = seo.title[language];
        const description = seo.description[language];
        const canonical = `${siteUrl}${seo.path === '/' ? '/' : seo.path}`;

        document.title = title;
        document.documentElement.setAttribute('lang', language);

        setMeta('name', 'description', description);
        setLink('canonical', canonical);

        setMeta('property', 'og:title', title);
        setMeta('property', 'og:description', description);
        setMeta('property', 'og:url', canonical);
        setMeta('property', 'og:type', 'website');
        setMeta('property', 'og:site_name', 'Met Maät');
        setMeta('property', 'og:locale', language === 'nl' ? 'nl_NL' : 'en_US');
        setMeta('property', 'og:image', ogImage);
        setMeta('property', 'og:image:secure_url', ogImage);
        setMeta('property', 'og:image:type', 'image/jpeg');
        setMeta('property', 'og:image:width', String(ogImageWidth));
        setMeta('property', 'og:image:height', String(ogImageHeight));
        setMeta('property', 'og:image:alt', ogImageAlt);

        setMeta('name', 'twitter:card', 'summary_large_image');
        setMeta('name', 'twitter:title', title);
        setMeta('name', 'twitter:description', description);
        setMeta('name', 'twitter:image', ogImage);

        if (seo.indexable) {
            document.head.querySelector('meta[name="robots"]')?.remove();
        } else {
            setMeta('name', 'robots', 'noindex,follow');
        }
    }, [pathname, language]);

    return null;
};

export default Seo;
